// A site can be visited more than once; only adjacent operations share a visit.
export function groupConsecutiveDriverStops(steps, getSiteKey) {
  const groups = [];
  const visitsBySite = new Map();
  for (const step of steps) {
    const siteKey = getSiteKey(step);
    const previous = groups[groups.length - 1];
    if (previous?.siteKey === siteKey) {
      previous.steps.push(step);
      continue;
    }
    const visit = visitsBySite.get(siteKey) || 0;
    visitsBySite.set(siteKey, visit + 1);
    const visitId = step.step_id || step.loading_step_id || step.delivery_id || visit;
    groups.push({
      siteKey,
      // Preserve existing links to the first visit, disambiguate later visits.
      groupKey: visit === 0 ? siteKey : `${siteKey}::visit:${encodeURIComponent(visitId)}`,
      steps: [step],
    });
  }
  return groups;
}

export function isDriverPortalVisitComplete(group) {
  const operations = group?.stepType === "loading" ? group.loadingActions : group?.commandRows;
  return Array.isArray(operations) && operations.length > 0 && operations.every((operation) => (
    group.stepType === "loading" ? operation.isEffectivelyCompleted : operation.isConfirmed
  ));
}

export function countDriverPortalOrders(steps) {
  return new Set(steps.map((step) => (
    step.order_id || step.linked_delivery?.order_id || step.linked_delivery_id
    || step.delivery_id || step.step_id
  )).filter(Boolean)).size;
}

export function buildDriverPortalQuantityDefaults(deliveries, loadings) {
  return Object.fromEntries([
    ...deliveries.map((command) => [command.key, String(
      command.actualDeliveredQuantityTons ?? command.actualLoadedQuantityTons ?? command.quantityTons ?? "",
    )]),
    ...loadings.map((action) => [action.key, String(action.actualLoadedQuantityTons ?? action.quantityTons ?? "")]),
  ]);
}

export function reconcileDriverPortalQuantityDrafts(drafts, previousDefaults, defaults, completedKeys, sameContext) {
  const completed = new Set(completedKeys);
  return Object.fromEntries(Object.entries(defaults).map(([key, value]) => [
    key,
    sameContext && !completed.has(key) && Object.hasOwn(drafts, key)
      && Object.hasOwn(previousDefaults, key) && drafts[key] !== previousDefaults[key]
      ? drafts[key]
      : value,
  ]));
}

export function isDriverPortalQuantityConfirmationCurrent(pending, deliveries, loadings) {
  if (!pending) return true;
  const subject = pending.kind === "loading" ? pending.action : pending.command;
  const current = (pending.kind === "loading" ? loadings : deliveries).find((item) => item.key === subject?.key);
  return Boolean(current && !current.isConfirmed && !current.isEffectivelyCompleted && !current.isBlocked
    && current.publicationRevision === subject.publicationRevision
    && current.executionRevision === subject.executionRevision
    && current.quantityTons === subject.quantityTons
    && current.actualLoadedQuantityTons === subject.actualLoadedQuantityTons);
}
